import {
	COMMUNITY_AIRDROP,
	type ExtensionConfig,
	STANDARD_LAUNCH,
	VAULTED_LAUNCH,
} from "@repo/shared-types";
import { motion } from "framer-motion";
import { useState } from "react";

import { cardHover } from "../../lib/animations";

type TemplateSelectorProps = {
	onTemplateSelect: (template: {
		extensions: ExtensionConfig[];
	}) => void;
};

const templates = [
	{
		...STANDARD_LAUNCH,
		icon: "🚀",
		features: ["MEV protection", "Fee collection", "Standard trading"],
	},
	{
		...VAULTED_LAUNCH,
		icon: "🔒",
		features: ["MEV protection", "Supply lock vault", "Configurable lock period"],
	},
	{
		...COMMUNITY_AIRDROP,
		icon: "🎁",
		features: ["MEV protection", "Community airdrop", "Merkle tree verified"],
	},
];

export function TemplateSelector({ onTemplateSelect }: TemplateSelectorProps) {
	const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

	const handleTemplateClick = (template: (typeof templates)[0]) => {
		setSelectedTemplate(template.name);
		onTemplateSelect({
			extensions: template.extensions,
		});
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
			{templates.map((template) => (
				<motion.button
					key={template.name}
					type="button"
					className={`
						p-6 text-left border rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/30
						${
							selectedTemplate === template.name
								? "border-pastel-vibrant-blue bg-pastel-vibrant-blue/10"
								: "border-pastel-border hover:border-pastel-vibrant-blue/50"
						}
					`}
					variants={cardHover}
					initial="rest"
					whileHover="hover"
					whileTap="tap"
					onClick={() => handleTemplateClick(template)}
					style={{ position: "relative" }}
				>
					<div className="flex items-center space-x-3 mb-3">
						<span className="text-2xl" aria-hidden="true">
							{template.icon}
						</span>
						<h4 className="font-semibold text-pastel-black">{template.name}</h4>
					</div>

					<p className="text-sm text-pastel-gray mb-4">{template.description}</p>

					<div className="space-y-2">
						<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide">Features</p>
						<ul className="space-y-1">
							{template.features.map((feature) => (
								<li key={feature} className="flex items-center text-sm text-pastel-gray">
									<span className="w-1.5 h-1.5 bg-pastel-vibrant-blue rounded-full mr-2" />
									{feature}
								</li>
							))}
						</ul>
					</div>

					{/* Extensions Preview */}
					{template.extensions.length > 0 && (
						<div className="mt-4 pt-3 border-t border-pastel-border">
							<div className="text-xs">
								<span className="text-pastel-gray">Extensions:</span>
								<span className="ml-1 font-medium text-pastel-black">
									{template.extensions.map((ext) => ext.type).join(", ")}
								</span>
							</div>
						</div>
					)}

					{selectedTemplate === template.name && (
						<motion.div
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							className="absolute top-2 right-2 w-6 h-6 bg-pastel-vibrant-blue rounded-full flex items-center justify-center"
						>
							<span className="text-white text-sm">✓</span>
						</motion.div>
					)}
				</motion.button>
			))}
		</div>
	);
}
